/**
 * =============================================================================
 * TYPES FOR ENTITY COMMUNICATION SECTION
 * =============================================================================
 *
 * These types are used by the Entity Communication components in the
 * EntityAnalysisModal to display entity network communication data.
 *
 * @see EntityCommunicationSection
 * @see EntityCommunicationPod
 * @see EntityCommunicationPopover
 * =============================================================================
 */

/**
 * Direction of communication relative to the current entity
 */
export enum CommunicationDirection {
  INBOUND = 'inbound',
  OUTBOUND = 'outbound',
}

/**
 * Aggregated counts of communicating entities
 */
export interface EntityCommunicationCounts {
  /** Number of entities that contacted the current entity (inbound) */
  contactedCurrentEntity: number;
  /** Number of entities contacted by the current entity (outbound) */
  contactedByCurrentEntity: number;
}

/**
 * Detailed information about a communicating entity
 * Used in the popover table to show individual connections
 */
export interface CommunicatingEntityDetail {
  /** Connection timestamp (unix epoch) */
  timestamp: number;
  /** IP address of the communicating entity */
  entityIp: string;
  /** MAC address of the communicating entity (if available) */
  macAddress?: string;
  /** Number of connections */
  count: number;
  /** Port used in the connection */
  port: number;
  /** Protocol used (tcp, udp, etc.) */
  protocol: string;
  /** Direction relative to the current entity */
  direction: CommunicationDirection;
}

/**
 * Pod variant determines the visual style and data displayed
 */
export enum EntityCommunicationPodVariant {
  INBOUND = 'inbound',
  CURRENT = 'current',
  OUTBOUND = 'outbound',
}

/**
 * Props for the EntityCommunicationPod component
 */
export interface EntityCommunicationPodProps {
  variant: EntityCommunicationPodVariant;
  entityCount?: number;
  entityName?: string;
  isLoading?: boolean;
}
